import React, { useState, useEffect } from "react";
import {
  Button,
  message,
  Row,
  Col,
  Form,
  Modal,
  Input,
  DatePicker,
  Select,
} from "antd";
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  CloudDownloadOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { CSVLink } from "react-csv";
import moment from "moment";
import { useFetchConstant } from "apis/constant";
import { useGetUsers, patchUpdateUser, refetchUsers } from "apis/user";

import Title from "components/Title/Title";

import { withAppContext } from "contexts/AppContext/AppContext";
import { getLabelOfConstant } from "utils/general";
import { buildUserNewUri, buildUserUri, buildProfileUri } from "utils/routes";
import {
  getColumnFilterSearchProps,
  constructColumn,
  handleOnAggregationTableChange,
} from "utils/table/table";
import BulkCreateUserModal from "./components/bulkCreateModal";
import { UsersTable } from "./Users.styles";
import { getAllProducts } from "apis/product";
import { assignCutOffDate } from "apis/billing";
import Locale from "react-intl-universal";

const Users = ({ getLoggedInUser, isAdmin }) => {
  const history = useHistory();
  const [freeTrialForm] = Form.useForm();
  const [freeTrialModalVisible, setFreeTrialModalVisible] = useState(false);
  const [freeTrialLoading, setFreeTrialLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState({ currentPage: 1, limit: null });
  const [updatedUsers, setUpdatedUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const loggedInUser = getLoggedInUser();
  const [allProducts, setAllProducts] = useState([]);

  const {
    isLoading: isUsersLoading,
    paginatedData: users,
    total,
  } = useGetUsers(query);

  useEffect(() => {
    if (users) {
      getUsersSubscriptionStatus();
    }
  }, [users]);

  const getUsersSubscriptionStatus = async () => {
    const mode = "unfiltered";
    const res = await getAllProducts(mode);
    let products = [];
    if (res.status === 200) {
      products = res.products;
      setAllProducts(products);
    }

    setUserLoading(true);

    users.forEach((user) => {
      user.originalStatus = user.status;

      const now = Math.floor(Date.now() / 1000);
      //check if user has free trial
      if (user.subscriptionStartDate && user.subscriptionEndDate) {
        const subscriptionStartDate = Number(user.subscriptionStartDate);
        const subscriptionEndDate = Number(user.subscriptionEndDate);
        const remainingDaysLeft = Math.round(
          (subscriptionEndDate - now) / 86400
        );

        if (subscriptionEndDate >= now && subscriptionStartDate <= now) {
          user.status = `${Locale.get(
            "Users.freeTrial"
          )} (${remainingDaysLeft} ${Locale.get("Users.days")})`;
        }
      }

      //check if user subscribe to any plan
      if (user.subscriptionInfo) {
        const subscriptionStatus = user.subscriptionInfo.subscriptionStatus;
        const paid = user.subscriptionInfo.paid;
        const priceId = user.subscriptionInfo.priceId;
        const startDate = Number(user.subscriptionInfo.startDate);
        const endDate = Number(user.subscriptionInfo.endDate);
        const remainingFreeTrialDaysLeft = Math.round((endDate - now) / 86400);
        const outstandingDays = Math.round((now - startDate) / 86400);
        const subscriptionPlan = products.find(
          (product) =>
            product.stripePriceIdMonthly === priceId ||
            product.stripePriceIdAnually === priceId
        );

        if (
          (subscriptionStatus === "active" ||
            subscriptionStatus === "trialing") &&
          paid === true
        ) {
          user.status = subscriptionPlan
            ? subscriptionStatus === "active"
              ? subscriptionPlan.stripePriceIdMonthly === priceId
                ? "Monthly Subscription"
                : "Yearly Subscription"
              : `Free Trial (${remainingFreeTrialDaysLeft} days)`
            : user.status;
        } else if (subscriptionStatus === "past_due" && !paid) {
          user.status = subscriptionPlan
            ? `Outstanding (${outstandingDays} days)`
            : user.status;
        }
      }

      //change to inactive for display purpose only
      if (user.status === "active") {
        user.status = "inactive";
      }
    });

    setUpdatedUsers(users);
    setUserLoading(false);
  };

  const { selection: roles } = useFetchConstant("roles");
  const {
    isLoading: isAccountStatusesLoading,
    selection: accountStatuses,
    data: accountStatusesConst,
  } = useFetchConstant("accountStatuses");

  const columns = [
    {
      ...constructColumn(Locale.get("Users.fullName"), "name", {
        hasSorter: true,
      }),
      ...getColumnFilterSearchProps("name", "Search name"),
    },
    {
      ...constructColumn(Locale.get("Users.contactNumber"), "contact"),
      ...getColumnFilterSearchProps("contact", "Search Contact"),
    },
    {
      ...constructColumn(Locale.get("Users.email"), "email"),
      ...getColumnFilterSearchProps("email", "Search Email"),
    },
    {
      ...constructColumn(Locale.get("Users.roleType"), "role", {
        hasSorter: true,
      }),
      render: (role) => getLabelOfConstant(role, roles),
    },
    // {
    //   ...constructColumn("Status", "status", { hasSorter: true }),
    //   render: (status) => getLabelOfConstant(status, accountStatuses),
    // },
    {
      ...constructColumn(Locale.get("Users.status"), "status", {
        hasSorter: true,
      }),
      render: (status) => {
        switch (status) {
          case "active":
            return Locale.get("Users.active");

          case "inactive":
            return Locale.get("Users.inactive");

          default:
            return status;
        }

        // return <div>{status.charAt(0).toUpperCase() + status.slice(1)}</div>;
      },
    },
    {
      ...constructColumn(Locale.get("Users.account"), "status"),
      // render: (status, record) =>
      //   getAccountActivationRender(record._id, status),
      render: (status, record) => {
        const userId = record._id;
        const isSelf = loggedInUser._id === userId;
        const userOriginalStatus = record.originalStatus;

        if (!isSelf && (status === "inactive" || status[0] === "O")) {
          return (
            <Button
              icon={<CloseOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                const updatedStatus =
                  userOriginalStatus === "inactive"
                    ? accountStatusesConst.ACTIVE.code
                    : accountStatusesConst.INACTIVE.code;
                patchUpdateUser(userId, { status: updatedStatus }).then(() => {
                  message.success(Locale.get("Users.updateSuccess"));
                  refetchUsers();
                });
              }}
            >
              {userOriginalStatus === "inactive"
                ? Locale.get("Users.activate")
                : Locale.get("Users.deactivate")}
            </Button>
          );
        }
      },
    },
    {
      ...constructColumn("", "status"),
      render: (status, record) => {
        return (
          <Button
            icon={<PlusOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setFreeTrialModalVisible(true);
              freeTrialForm.setFieldsValue({
                name: record.name,
                status:
                  record.status.charAt(0).toUpperCase() +
                  record.status.slice(1),
                role: record.role,
                email: record.email,
              });
            }}
          >
            {Locale.get("Users.addFreeTrial")}
          </Button>
        );
      },
    },
  ];

  // Export CSV

  const [csv_data, set_csv_data] = useState([]);
  const [csv_header, set_csv_header] = useState([
    {
      label: "Creation Date",
      key: "createdAt",
    },
    {
      label: "Name",
      key: "name",
    },
    {
      label: "Contact",
      key: "contact",
    },
    {
      label: "Email",
      key: "email",
    },
    {
      label: "Role Type",
      key: "role",
    },
    {
      label: "Status",
      key: "status",
    },
    {
      label: "Company",
      key: "company",
    },
    {
      label: "Last Login",
      key: "lastLogin",
    },
  ]);

  var GetUser = useGetUsers({ currentPage: 1, limit: null });

  const generateCSV = async () => {
    const now = Math.floor(Date.now() / 1000);
    var order = [...GetUser.paginatedData];
    var csv_data = [];

    for (let i = 0; i < order.length; i++) {
      var user = order[i];

      //check if user has free trial
      if (user.subscriptionStartDate && user.subscriptionEndDate) {
        const subscriptionStartDate = Number(user.subscriptionStartDate);
        const subscriptionEndDate = Number(user.subscriptionEndDate);
        const remainingDaysLeft = Math.round(
          (subscriptionEndDate - now) / 86400
        );

        if (subscriptionEndDate >= now && subscriptionStartDate <= now) {
          user.status = `Free Trial (${remainingDaysLeft} days)`;
        }
      }

      //check if user subscribe to any plan
      if (user.subscriptionInfo) {
        const subscriptionStatus = user.subscriptionInfo.subscriptionStatus;
        const paid = user.subscriptionInfo.paid;
        const priceId = user.subscriptionInfo.priceId;
        const startDate = Number(user.subscriptionInfo.startDate);
        const endDate = Number(user.subscriptionInfo.endDate);
        const remainingFreeTrialDaysLeft = Math.round((endDate - now) / 86400);
        const outstandingDays = Math.round((now - startDate) / 86400);
        const subscriptionPlan = allProducts.find(
          (product) =>
            product.stripePriceIdMonthly === priceId ||
            product.stripePriceIdAnually === priceId
        );

        if (
          (subscriptionStatus === "active" ||
            subscriptionStatus === "trialing") &&
          paid === true
        ) {
          user.status = subscriptionPlan
            ? subscriptionStatus === "active"
              ? subscriptionPlan.stripePriceIdMonthly === priceId
                ? "Monthly Subscription"
                : "Yearly Subscription"
              : `Free Trial (${remainingFreeTrialDaysLeft} days)`
            : user.status;
        } else if (subscriptionStatus === "past_due" && !paid) {
          user.status = subscriptionPlan
            ? `Outstanding (${outstandingDays} days)`
            : user.status;
        }
      }

      //change to inactive for display purpose only
      if (user.status === "active") {
        user.status = "inactive";
      }

      csv_data.push({
        createdAt: user.createdAt,
        name: user.name,
        company: user.company,
        contact: user.contact,
        email: user.email,
        role: user.role,
        status: user.status,
        lastLogin: user.lastLogin,
      });
    }

    set_csv_data(csv_data);

    return true;
  };

  const getAccountActivationRender = (userId, currentStatus) => {
    const isSelf = loggedInUser._id === userId;
    const isInactive =
      !isAccountStatusesLoading &&
      currentStatus === accountStatusesConst.INACTIVE.code;
    const buttonText = isInactive ? "Activate" : "Deactivate";
    const buttonIcon = isInactive ? <CheckOutlined /> : <CloseOutlined />;

    if (!isSelf) {
      return (
        <Button
          icon={buttonIcon}
          onClick={(e) => {
            e.stopPropagation();
            const updatedStatus = isInactive
              ? accountStatusesConst.ACTIVE.code
              : accountStatusesConst.INACTIVE.code;
            patchUpdateUser(userId, { status: updatedStatus }).then(() => {
              message.success(Locale.get("Users.updateSuccess"));
              refetchUsers();
            });
          }}
        >
          {buttonText}
        </Button>
      );
    }
  };

  const handleOnRow = (record) => ({
    onClick: () => {
      if (loggedInUser._id === record._id) {
        history.push(buildProfileUri());
      } else {
        history.push(buildUserUri(record._id));
      }
    },
  });

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      md: { span: 7 },
      xl: { span: 7 },
    },
    wrapperCol: {
      xs: { span: 24 },
      md: { span: 16 },
      xl: { span: 16 },
    },
  };

  const formItemLayoutWithOutLabel = {
    wrapperCol: {
      xs: { span: 24 },
      md: { span: 16, offset: 8 },
      xl: { span: 16, offset: 7 },
    },
  };

  const HandleAddFreeTrial = async () => {
    const values = await freeTrialForm.validateFields();

    values.subscriptionStartDate = moment().unix();
    values.subscriptionEndDate = moment(values.subscriptionEndDate).unix();

    Modal.confirm({
      title: `${Locale.get("Users.freeTrialConfirmMsg")} ${values.name}?`,
      onOk: async () => {
        assignCutOffDate(values)
          .then((res) => {
            if (res.status === 200) {
              message.success(
                `${Locale.get("Users.freeTrialSuccessMsg")} ${values.name}`
              );
              setFreeTrialModalVisible(false);
              console.log("res assignCutOffDate", res);
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }
          })
          .catch((ex) => {
            console.log("error assignCutOffDate", ex);
            message.error(
              `Failed to add free trial to ${values.name}. Please try again later.`
            );
          });
      },
    });
  };

  const FreeTrialModal = (
    <Modal
      visible={freeTrialModalVisible}
      title={Locale.get("Users.freeTrial")}
      afterClose={() => {
        freeTrialForm.resetFields();
      }}
      onCancel={() => {
        setFreeTrialModalVisible(false);
      }}
      onOk={HandleAddFreeTrial}
      okButtonProps={{ loading: freeTrialLoading }}
      cancelButtonProps={{ disabled: freeTrialLoading }}
    >
      <Form form={freeTrialForm} {...formItemLayout}>
        <Form.Item
          label={Locale.get("Users.name")}
          name={"name"}
          rules={[
            {
              required: true,
              message: Locale.get("Users.errMsgName"),
            },
          ]}
        >
          <Input readOnly />
        </Form.Item>
        <Form.Item
          label={Locale.get("Users.email")}
          name={"email"}
          rules={[
            {
              required: true,
              message: Locale.get("Users.errMsgEmail"),
            },
          ]}
        >
          <Input readOnly />
        </Form.Item>
        {/* <Form.Item
          label="Status"
          name={"status"}
          rules={[
            {
              required: true,
              message: "Status is required",
            },
          ]}
        >
          <Input readOnly />
        </Form.Item> */}
        <Form.Item
          label={Locale.get("Users.role")}
          name={"role"}
          rules={[
            {
              required: true,
              message: Locale.get("Users.errMsgRole"),
            },
          ]}
        >
          <Select
            placeholder="Select Role"
            style={{ width: "100%" }}
            options={roles}
          ></Select>
        </Form.Item>
        <Form.Item
          name="subscriptionEndDate"
          label={Locale.get("Users.freeTrialEndDate")}
          rules={[
            {
              required: true,
              message: Locale.get("Users.errMsgTrialEndDate"),
            },
          ]}
        >
          <DatePicker
            format={"DD/MM/YYYY"}
            style={{ width: "100%" }}
            disabledDate={(current) => {
              // disable dates before today's date
              return current && current < moment().startOf("day");
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title>{Locale.get("Users.pageTitle")}</Title>
        </Col>

        <Col>
          <Row gutter={24}>
            <Col>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setVisible(true)}
                style={{ color: "#d7a301", borderColor: "#d7a301" }}
              >
                {Locale.get("Users.bulkCreateUsers")}
              </Button>
            </Col>
            <Col>
              <CSVLink
                data={csv_data}
                headers={csv_header}
                filename={`${moment().format("DD-MMM-YYYY")}_Users.csv`}
                onClick={() => generateCSV()}
              >
                <Button
                  icon={<CloudDownloadOutlined style={{ color: "#d7a301" }} />}
                  style={{ color: "#d7a301", borderColor: "#d7a301" }}
                >
                  {Locale.get("Users.csv")}
                </Button>
              </CSVLink>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => history.push(buildUserNewUri())}
              >
                {Locale.get("Users.addNewUsers")}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <UsersTable
        loading={userLoading}
        columns={columns}
        dataSource={updatedUsers}
        onRow={handleOnRow}
        onChange={(pagination, filters, sorter) => {
          handleOnAggregationTableChange(
            { pagination, filters, sorter },
            setQuery
          );
        }}
        scroll={{
          x: 1300,
        }}
        pagination={{ total, current: query.currentPage }}
        rowKey={(record) => record._id}
        showSorterTooltip={false}
      />

      <BulkCreateUserModal
        visible={visible}
        onCancel={() => setVisible(false)}
      />
      {FreeTrialModal}
    </div>
  );
};

export default withAppContext(Users);
